<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back -->
    <div class="back-home text-left mb-3">
      <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/kids-device', query: { profileId, serialNumber } })">
        <i class="fas fa-arrow-left"></i> <b>Back to Home</b>
      </a>
    </div>

    <!-- Parent Info -->
    <div class="card parent-profile p-3 mb-4">
      <h5>Parent Profile</h5><hr>
      <p><b>Name:</b> {{ parentProfile.name }}</p>
      <p><b>Email:</b> <span class="badge bg-primary text-white">{{ parentProfile.email }}</span></p>
    </div>

    <!-- Note Section -->
    <div class="card info-note mb-4">
        <p class="mb-0">
            <strong>Note:</strong> Giving Delegated Access means the user can manage your kids on your behalf.
            They may view or control kids' devices depending on the selected access level.
        </p>
    </div>


    <!-- Delegated Access (full-width) -->
    <div class="card p-3 mb-4 w-100">
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Delegated Access</h5>
        <button class="btn btn-primary" @click="showAddModal = true">Add Access</button>
      </div>

      <div class="table-responsive mt-3">
        <!-- Table when records exist -->
        <table class="table" v-if="delegatedAccessList.length">
          <thead>
            <tr>
              <th>User</th>
              <th>Access Level</th>
              <th>Allowed Kids</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in delegatedAccessList" :key="index">
              <td>
                <div>
                  <strong>{{ item.name }}</strong><br />
                  <small class="text-muted">{{ item.email }}</small>
                </div>
              </td>
              <td>{{ item.accessLevel }}</td>
              <td>{{ item.allowedKids.join(', ') }}</td>
              <td>
                <span :class="['badge', statusBadgeClass(item.status)]">
                  {{ item.status }}
                </span>
              </td>
              <td>
                <div class="action-buttons">
                  <!--<button class="btn btn-sm btn-outline-secondary" @click="editAccess(index)">Edit</button>-->
                  <button class="btn btn-sm btn-outline-danger" @click="openConfirmationModal(item.id, 'Revoke')">Revoke</button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- No records message (centered & full width) -->
        <div v-else class="text-center text-muted py-2 w-100">
          No delegated access found.
        </div>
      </div>
    </div>


    <!-- 📨 Access Received from Others -->
    <div class="card p-3 mb-4" v-if="accessReceived.length">
      <h5>Access Received from Others</h5>
      <div class="table-responsive">
        <table class="table mt-3">
          <thead>
            <tr>
              <th>From</th>
              <th>Access Level</th>
              <th>Kids</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in accessReceived" :key="index">
              <td>
                <div>
                <strong>{{ item.name }}</strong><br />
                <small class="text-muted">{{ item.email }}</small>
                </div>
            </td>
              <td>{{ item.accessLevel }}</td>
              <td>{{ item.allowedKids.join(', ') }}</td>
              <td>
                <div class="action-buttons justify-content-center">
                  <template v-if="item.status == 'Pending'">
                    <button class="btn btn-sm btn-success" style="min-width: 80px;" @click="openConfirmationModal(item.id, 'Accepted')">Accept</button>
                    <button class="btn btn-sm btn-danger" style="min-width: 80px;" @click="openConfirmationModal(item.id, 'Rejected')">Reject</button>
                  </template>
                  <template v-else>
                    <span :class="['badge', statusBadgeClass(item.status)]">
                      {{ item.status }}
                    </span>
                  </template>
                </div>
              </td>

            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ➕ Modal: Add / Edit Access -->
    <div v-if="showAddModal" class="modal-mask">
      <div class="modal-wrapper">
        <div class="modal-container">
          <h5>{{ isEditing ? 'Edit Delegated Access' : 'Add Delegated Access' }}</h5>

          <div class="form-group text-left">
            <label><b>Name</b></label>
            <input v-model="newDelegate.name" class="form-control" type="text" placeholder="Delegate name" />
          </div>
          <div class="form-group text-left" v-if="parentProfile">
            <label><b>Email</b> <small>(Please enter only your username, not the full email address.)</small></label>
            <div class="input-group">
                <input v-model="newDelegate.email" class="form-control" type="text" placeholder="Delegate username" />
                <div class="input-group-append">
                    <span class="input-group-text">@{{ parentProfile.email.split('@')[1] }}</span>
                </div>
            </div>

          </div>
          <div class="form-group text-left">
            <label><b>Access Level</b></label>
            <select v-model="newDelegate.accessLevel" class="form-control">
              <option value="full">Full</option>
            </select>
          </div>
          <div class="form-group text-left">
            <label><b>Allowed Kids</b></label>
            <div v-for="kid in kidsList" :key="kid._id">
              <input type="checkbox" :id="kid._id" :value="kid._id" v-model="newDelegate.allowedKids" />
              <label :for="kid.id">&nbsp;{{ kid.profile_name }}</label>
            </div>
          </div>
          <div class="text-right mt-3">
            <button class="btn btn-secondary mr-2" @click="closeModal">Cancel</button>
            <button class="btn btn-success" @click="saveDelegatedAccess">
              {{ isEditing ? 'Update' : 'Add' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm Modal -->
    <div class="modal fade" id="confirmAccessModal" tabindex="-1" role="dialog" aria-labelledby="confirmAccessModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="confirmAccessModalLabel">Confirm Action</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            Are you sure you want to <strong>{{ pendingAction.status }}</strong> this delegate access request?
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="confirmDelegateAccess">Yes, Confirm</button>
          </div>
        </div>
      </div>
    </div>


    <KidFooter />
  </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import UserService from "@/services/UserService";

export default {
  name: 'DelegatedAccessPage',
  components: {
    KidHeader,
    KidFooter,
  },
  data() {
  return {
    profileId: this.$route.query.profileId,
    serialNumber: this.$route.query.serialNumber,

    parentProfile: {
      name: "Loading...",
      email: "Loading..."
    },

    delegatedAccessList: [],
    kidsList: [],
    accessReceived: [],

    showAddModal: false,
    isEditing: false,
    editingIndex: null,

    newDelegate: {
      name: '',
      email: '',
      accessLevel: 'full',
      allowedKids: []
    },
    pendingAction: {
      id: null,
      status: ''
    }
  };
},
methods: {
  async fetchParentProfile() {
    await UserService.fetchParentProfile()
        .then((response) => {
          this.parentProfile = response.data.data;
          this.kidsList = response.data.kidsList
          this.delegatedAccessList = response.data.delegatedAccessList
          this.accessReceived = response.data.accessReceivedList
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
        });
  },
  closeModal() {
    this.showAddModal = false;
    this.isEditing = false;
    this.newDelegate = { name: '', email: '', accessLevel: 'view', allowedKids: [] };
  },
  saveDelegatedAccess() {
    if (!this.newDelegate.email || !this.newDelegate.name || !this.newDelegate.allowedKids.length) {
      this.$toast.error("Please fill all required fields.");
      return;
    }
    let data = { ...this.newDelegate }; // spread operator to clone
    data.email = data.email + "@" + this.parentProfile.email.split('@')[1];
    UserService.saveDelegatedAccess(data)
        .then((response) => {
          this.$toast.success(response.data.respones_msg);
          this.fetchParentProfile();
          this.closeModal();     
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
        });
  },
  editAccess(index) {
    const access = this.delegatedAccessList[index];
    this.newDelegate = { ...access };
    this.editingIndex = index;
    this.isEditing = true;
    this.showAddModal = true;
  },
  revokeAccess(index) {
    
    this.$toast.success("Access revoked.");
  },
  openConfirmationModal(id, status) {
    this.pendingAction.id = id;
    this.pendingAction.status = status;
    $('#confirmAccessModal').modal('show');
  },
  
  confirmDelegateAccess() {
    const { id, status } = this.pendingAction;

    UserService.updateDelegateAccessStatus({ id, status })
      .then((response) => {
        this.$toast.success(response.data.respones_msg);
        this.fetchParentProfile();
      })
      .catch((e) => {
        this.$toast.error(e.response.data.respones_msg);
      })
      .finally(() => {
        // Hide the modal
        $('#confirmAccessModal').modal('hide');
      });
  },

  statusBadgeClass(status) {
    switch (status) {
      case 'Accepted': return 'bg-success text-white';
      case 'Pending': return 'bg-warning text-dark';
      case 'Rejected': return 'bg-danger text-white';
      default: return 'bg-secondary text-white';
    }
  },
},
watch: {
  kidsList: {
    immediate: true,
    handler(newList) {
      if (Array.isArray(newList)) {
        this.newDelegate.allowedKids = newList.map(kid => kid._id);
      }
    }
  }
},
mounted() {
  this.fetchParentProfile();
}

}
</script>
<style scoped>
.card {
  border-radius: 10px;
  background: #fff;
  padding: 20px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}
.parent-profile {
  width: 100%;
  margin: 0 0 20px 0;
  padding: 20px;
  background: #fff;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  border-radius: 10px;
}


.back-home {
  display: block;
  width: 100%;
  margin-top: 10px;
  margin-bottom: 10px;
  font-size: 1rem;
  z-index: 1;
  position: relative;
}

.badge {
  padding: 5px 10px;
  font-size: 0.9rem;
  border-radius: 5px;
}

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px; /* Add padding to prevent clipping */
}

.modal-wrapper {
  width: 100%;
  max-width: 500px;
}

.modal-container {
  background: white;
  padding: 20px;
  border-radius: 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.alert {
  border-radius: 8px;
  padding: 15px;
  background: #e9f7fe;
  color: #31708f;
  border: 1px solid #bce8f1;
}

@media (max-width: 576px) {
  .table-responsive {
    overflow-x: auto;
  }
  .modal-container {
    padding: 15px;
  }
}

/* Align Edit/Revoke buttons horizontally */
td .action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Optional: Adjust button sizes for smaller screens */
@media (max-width: 576px) {
  td .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
}

.info-note {
  background: #e9f7fe;
  border: 1px solid #bce8f1;
  color: #31708f;
  padding: 15px;
  border-radius: 10px;
}


</style>
